import { z } from 'zod';

/**
 * Base error class for validation-related errors
 */
export class ValidationError extends Error {
  public readonly code = 'VALIDATION_ERROR';
  public readonly details: z.ZodIssue[];

  constructor(message: string, details: z.ZodIssue[] = []) {
    super(message);
    this.name = 'ValidationError';
    this.details = details;

    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ValidationError);
    }
  }

  /**
   * Create a ValidationError from a ZodError
   */
  static fromZodError(zodError: z.ZodError): ValidationError {
    const message = `Validation failed: ${zodError.message}`;
    return new ValidationError(message, zodError.issues);
  }

  /**
   * Get a formatted error message for user display
   */
  getFormattedMessage(): string {
    if (this.details.length === 0) {
      return this.message;
    }

    const issueMessages = this.details.map(issue => {
      const path = issue.path.length > 0 ? ` at "${issue.path.join('.')}"` : '';
      return `${issue.message}${path}`;
    });

    return `${this.message}:\n${issueMessages.map(msg => `  - ${msg}`).join('\n')}`;
  }

  /**
   * Get flattened field errors suitable for form validation
   */
  getFieldErrors(): Record<string, string[]> {
    const fieldErrors: Record<string, string[]> = {};

    for (const issue of this.details) {
      const path = issue.path.join('.');
      if (!fieldErrors[path]) {
        fieldErrors[path] = [];
      }
      fieldErrors[path].push(issue.message);
    }

    return fieldErrors;
  }
}

/**
 * Generic API error class for HTTP-related errors
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly details?: unknown;

  constructor(
    message: string,
    statusCode: number = 500,
    code: string = 'API_ERROR',
    details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Create a BadRequest (400) error
   */
  static badRequest(
    message: string = 'Bad Request',
    details?: unknown
  ): ApiError {
    return new ApiError(message, 400, 'BAD_REQUEST', details);
  }

  /**
   * Create an Unauthorized (401) error
   */
  static unauthorized(
    message: string = 'Unauthorized',
    details?: unknown
  ): ApiError {
    return new ApiError(message, 401, 'UNAUTHORIZED', details);
  }

  /**
   * Create a Forbidden (403) error
   */
  static forbidden(message: string = 'Forbidden', details?: unknown): ApiError {
    return new ApiError(message, 403, 'FORBIDDEN', details);
  }

  /**
   * Create a NotFound (404) error
   */
  static notFound(message: string = 'Not Found', details?: unknown): ApiError {
    return new ApiError(message, 404, 'NOT_FOUND', details);
  }

  /**
   * Create a Conflict (409) error
   */
  static conflict(message: string = 'Conflict', details?: unknown): ApiError {
    return new ApiError(message, 409, 'CONFLICT', details);
  }

  /**
   * Create an Internal Server Error (500) error
   */
  static internalServerError(
    message: string = 'Internal Server Error',
    details?: unknown
  ): ApiError {
    return new ApiError(message, 500, 'INTERNAL_SERVER_ERROR', details);
  }

  /**
   * Convert the error to a JSON object suitable for API responses
   */
  toJSON(): {
    error: true;
    message: string;
    code: string;
    statusCode: number;
    details?: unknown;
  } {
    const result: {
      error: true;
      message: string;
      code: string;
      statusCode: number;
      details?: unknown;
    } = {
      error: true,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
    };

    if (this.details) {
      result.details = this.details;
    }

    return result;
  }
}
